import { formatDateForServiceStack } from "./date-utils";

export interface SensorReadingGraphResponse {
    series: GraphSeries[];
    annotations: GraphAnnotation[];
}

export interface GraphSeries {
    name: string;
    sensorId: number;
    data: GraphCoordinate[];
    color: string;
    measurementType: "Temperature" | "Humidity" | "Pressure" | "PressureDiff" | "OpenClose" | "BatteryLevel";
}

export interface GraphAnnotation {
    name: string;
    value: number;
}

export interface GraphCoordinate {
    x: string;
    y: number;
}

export interface Location {
    id: number;
    name: string;
}

export enum SensorStatus { 
    All = 0, Online = 1,  Offline = 2,  Alerting = 3 
}

export interface OptionItem<TId> {
    id: TId;
    text: string;
}

export interface LocationOptionItem extends OptionItem<number> {
}

export interface DeviceOptionItem extends OptionItem<number> {
    locationIds: number[];
}

export interface SensorOptionItem extends OptionItem<number> {
    deviceId: number;
}

export interface UserOptionItem extends OptionItem<string> {
    locationIds: number[];
    viewAllLocations: boolean;
}

export function getTemperatureReadingsByDevice(deviceId: number, fromDate: Date, toDate: Date): JQueryPromise<SensorReadingGraphResponse> {
    return $.ajax({
        url: `/temperature?format=json&DeviceId=${deviceId}&FromDate=${formatDateForServiceStack(fromDate)}&EndDate=${formatDateForServiceStack(toDate)}`,
        cache: false,
        type: "GET",
    }).promise() as any;
}

export function getTemperatureReadingsBySensor(sensorId: number, fromDate: Date, toDate: Date): JQueryPromise<SensorReadingGraphResponse> {
    return $.ajax({
        url: `/temperature?format=json&SensorId=${sensorId}&FromDate=${formatDateForServiceStack(fromDate)}&EndDate=${formatDateForServiceStack(toDate)}`,
        cache: false,
        type: "GET",
    }).promise() as any;
}

export function getTemperatureReadingsBySensorGroup(fromDate: Date, toDate: Date, sensorGroupId: number): JQueryPromise<SensorReadingGraphResponse> {
    return $.ajax({
        url: `/temperature?format=json&FromDate=${formatDateForServiceStack(fromDate)}&EndDate=${formatDateForServiceStack(toDate)}&SensorGroupId=${sensorGroupId}`,
        cache: false,
        type: "GET",
    }).promise() as any;
}

export function setDashboardChartOrder(currentLocation: Location, charts: { sensorGroupId?: number, deviceId?: number }[]): JQueryPromise<VoidFunction> {        
    return $.ajax({
        url: `/chart-order`,
        data: JSON.stringify({
            locationId: currentLocation.id,
            charts: charts,
        }),
        contentType: "application/json",
        cache: false,
        type: "POST",
    }).promise() as any;
}

/* retrieves data pre-loaded through <link rel='preload'> tags */
export async function getPrefetchedData<TId>(preloadLinkId: string): Promise<Array<OptionItem<TId>>> {
    const preloadElement = $(`link[rel='preload'][id='${preloadLinkId}']`);
    const href = preloadElement.attr("href");
    const data = await fetch(href, { credentials: "include" });
    return await data.json();
}

export async function getPrefetchedLocations(): Promise<Array<LocationOptionItem>> {
    return (await getPrefetchedData("locations-prefetch")) as Array<LocationOptionItem>;
}

export async function getPrefetchedDevices(): Promise<Array<DeviceOptionItem>> {
    return (await getPrefetchedData("devices-prefetch")) as Array<DeviceOptionItem>;
}

export async function getPrefetchedSensors(): Promise<Array<SensorOptionItem>> {
    return (await getPrefetchedData("sensors-prefetch")) as Array<SensorOptionItem>;
}

export async function getPrefetchedUsers(): Promise<Array<UserOptionItem>> {
    return (await getPrefetchedData("users-prefetch")) as Array<UserOptionItem>;
}
